/**
 * Vidya Yoga — education report for parents buying for their children.
 */
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest, VidyaYogaResponse } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function VidyaYogaPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const reportMut = useMutation<VidyaYogaResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.vidyaYoga(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          🎓 {t('विद्या योग — बालक का शैक्षिक मार्गदर्शन', 'Vidya Yoga — Child\'s Education Guidance')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            'पाँचवाँ भाव, उसका स्वामी एवं संबंधित ग्रह — शिक्षण-शैली एवं उपयुक्त धाराएँ।',
            "5th house, its lord, and supporting planets — learning style and suitable streams.",
          )}
        </p>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('गणना हो रही है…', 'Computing…')}
        </p>
      )}

      {report && <VidyaResult report={report} lang={lang} t={t} />}
    </div>
  );
}

function VidyaResult({
  report,
  lang,
  t,
}: {
  report: VidyaYogaResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const takeaways = lang === 'hi' ? report.takeaways_hi : report.takeaways_en;
  return (
    <article className="space-y-5">
      {takeaways && takeaways.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-saffron-700/40">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-saffron-300">
            {t('मुख्य निष्कर्ष', 'Key Takeaways')}
          </h3>
          <ul className="ml-5 list-disc space-y-2 text-sm text-stone-100">
            {takeaways.map((tk, i) => (
              <li key={i}>{tk}</li>
            ))}
          </ul>
        </section>
      )}
      <Section title={t('1. पंचम भाव विश्लेषण', '1. 5th House Analysis')}>
        <dl className="space-y-2 text-sm">
          <DefRow
            label={t('5वें भाव में ग्रह', 'Planets in 5th house')}
            value={
              (lang === 'hi' ? report.fifth_house_planets_hi : report.fifth_house_planets_en).join(', ')
              || t('कोई नहीं', 'None')
            }
          />
          <DefRow
            label={t('5वें-स्वामी की स्थिति', "5th lord's position")}
            value={lang === 'hi' ? report.fifth_lord_position_hi : report.fifth_lord_position_en}
          />
          <DefRow
            label={t('4वें भाव में ग्रह (नींव)', '4th house planets (foundation)')}
            value={
              (lang === 'hi' ? report.fourth_house_planets_hi : report.fourth_house_planets_en).join(', ')
              || t('कोई नहीं', 'None')
            }
          />
        </dl>
      </Section>

      <Section title={t('2. उपयुक्त शैक्षिक धाराएँ', '2. Suitable Education Streams')}>
        <ol className="space-y-3">
          {report.streams.map((s, i) => (
            <li
              key={i}
              className="rounded-md bg-stone-900/60 p-3 ring-1 ring-stone-800"
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-stone-100">
                  {lang === 'hi' ? s.name_hi : s.name_en}
                </p>
                <span className="text-[11px] tabular-nums text-saffron-300">
                  {s.confidence}/10
                </span>
              </div>
              <p className="mt-1 text-[12px] text-stone-400">
                {lang === 'hi' ? s.why_hi : s.why_en}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      <Section title={t('3. सीखने की शैली', '3. Learning Style')}>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.learning_style_hi : report.learning_style_en}
        </p>
      </Section>

      <Section title={t('4. एकाग्रता', '4. Concentration')}>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.concentration_hi : report.concentration_en}
        </p>
      </Section>

      <Section title={t('5. उच्च-शिक्षा', '5. Higher Education')}>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.higher_education_hi : report.higher_education_en}
        </p>
      </Section>

      <Section title={t('6. माता-पिता के लिए मार्गदर्शन', '6. Parental Guidance')}>
        <ul className="space-y-1 text-sm text-stone-100">
          {(lang === 'hi' ? report.parental_guidance_hi : report.parental_guidance_en).map((g, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-saffron-400">●</span>
              <span>{g}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t('सारांश', 'Summary')}>
        <p className="text-sm italic leading-relaxed text-stone-100">
          {lang === 'hi' ? report.summary_hi : report.summary_en}
        </p>
      </Section>

      {report.inner_practice && (
        <section className="rounded-2xl bg-emerald-950/30 p-5 ring-1 ring-emerald-800/40">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-200">
            {t('साधना', 'Inner Practice')}
          </h2>
          <p className="text-sm text-stone-200">
            {lang === 'hi' ? report.inner_practice.ishta_deva.text_hi
                           : report.inner_practice.ishta_deva.text_en}
          </p>
          {report.inner_practice.affirmations.length > 0 && (
            <ul className="ml-5 mt-3 list-disc space-y-1 text-sm italic text-emerald-200">
              {report.inner_practice.affirmations.map((af, i) => (
                <li key={i}>"{lang === 'hi' ? af.affirmation_hi : af.affirmation_en}"</li>
              ))}
            </ul>
          )}
          <p className="mt-3 rounded-md bg-stone-900/40 p-2 text-xs italic text-stone-300">
            {lang === 'hi' ? report.inner_practice.practice_instruction_hi
                           : report.inner_practice.practice_instruction_en}
          </p>
        </section>
      )}

      {report.methods && report.methods.length > 0 && (
        <section className="rounded-2xl bg-stone-900/40 p-4 text-xs text-stone-500 ring-1 ring-stone-800">
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500">
            {t('विधि एवं स्रोत', 'Methodology & Sources')}
          </h3>
          <ul className="space-y-1 leading-relaxed">
            {report.methods.map((m, i) => (
              <li key={i}>
                <span>{lang === 'hi' ? m.method_hi : m.method_en}</span>
                <span className="italic text-stone-600">
                  {' '}— {lang === 'hi' ? m.source_hi : m.source_en}
                </span>
              </li>
            ))}
          </ul>
          {(report.disclaimer_hi || report.disclaimer_en) && (
            <p className="mt-3 italic text-stone-600">
              {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
            </p>
          )}
        </section>
      )}

      <p className="text-center text-[10px] text-stone-600">{report.rules_version}</p>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
      <h3 className="mb-3 text-base font-semibold text-saffron-200">{title}</h3>
      {children}
    </section>
  );
}

function DefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <dt className="text-saffron-300">{label}:</dt>
      <dd className="text-stone-100">{value}</dd>
    </div>
  );
}
